import { useContext } from 'react';

import {
	WHITEBOARD_VERSION_HISTORY_LOAD_RESTORE_MODAL,
	WHITEBOARD_VERSION_HISTORY_RESTORE_EXPERIENCE,
	WHITEBOARD_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
	SLIDE_VERSION_HISTORY_LOAD_RESTORE_MODAL,
	SLIDE_VERSION_HISTORY_RESTORE_EXPERIENCE,
	SLIDE_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
	DATABASE_VERSION_HISTORY_LOAD_RESTORE_MODAL,
	DATABASE_VERSION_HISTORY_RESTORE_EXPERIENCE,
	DATABASE_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { isVersionHistoryContentType } from './contentTypeConstants.ts';
import type { VersionHistoryContentType } from './contentTypeConstants.ts';

type VersionHistoryExperience = 'restoreModal' | 'restoreExperience' | 'sidePanel';

type useVersionHistoryExperienceTrackingType = {
	contentType: string;
	experience: VersionHistoryExperience;
	task: 'start' | 'succeed';
};

const experienceMap = {
	whiteboard: {
		restoreModal: WHITEBOARD_VERSION_HISTORY_LOAD_RESTORE_MODAL,
		restoreExperience: WHITEBOARD_VERSION_HISTORY_RESTORE_EXPERIENCE,
		sidePanel: WHITEBOARD_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
	},
	slide: {
		restoreModal: SLIDE_VERSION_HISTORY_LOAD_RESTORE_MODAL,
		restoreExperience: SLIDE_VERSION_HISTORY_RESTORE_EXPERIENCE,
		sidePanel: SLIDE_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
	},
	database: {
		restoreModal: DATABASE_VERSION_HISTORY_LOAD_RESTORE_MODAL,
		restoreExperience: DATABASE_VERSION_HISTORY_RESTORE_EXPERIENCE,
		sidePanel: DATABASE_VERSION_HISTORY_SIDE_PANEL_EXPERIENCE,
	},
} satisfies Record<VersionHistoryContentType, Record<VersionHistoryExperience, string>>;

export const useVersionHistoryExperienceTracking = (): {
	trackExperience: ({
		contentType,
		experience,
		task,
	}: useVersionHistoryExperienceTrackingType) => void;
} => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const trackExperience = ({
		contentType,
		experience,
		task,
	}: useVersionHistoryExperienceTrackingType): void => {
		if (!contentType || !experience || !task) {
			return;
		}
		if (isVersionHistoryContentType(contentType)) {
			const experienceName = experienceMap[contentType][experience];
			if (experienceName) {
				if (task === 'start') {
					experienceTracker.start({ name: experienceName });
				} else {
					experienceTracker.succeed({ name: experienceName });
				}
			}
		}
	};

	return {
		trackExperience,
	};
};
